import type { ReactElement } from 'react';
import { useState } from 'react';

import { ErrorMessage, ApiRoute } from 'config';

import { put } from 'utils/network';
import type IApiResponse from 'models/IApiResponse';

import Button from 'react-bootstrap/Button';
import { IGiveaway } from 'models/Giveaway';

type Props = {
  showSuccessBanner: (email: string) => void;
  showInfobanner: () => void;
  giveawayToSend: IGiveaway;
};

function GiveawayForm({
  showSuccessBanner,
  showInfobanner,
  giveawayToSend,
}: Props): ReactElement {
  const [fullName, setFullName] = useState('');
  const [message, setMessage] = useState([
    'Hi! No need to plan dinner next week because I’m sending',
    'you a free Purple Carrot, box so you can try their delicious',
    'plant-based meals. (They’re really good.)\nEnjoy!',
  ].join(' '));

  const [email, setEmail] = useState('');
  const [isSubmitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const sendGiveaway = async () => {
    const payload = {
      giveaway: {
        recipient_name: fullName,
        recipient_email: email,
        personalized_message: message,
      },
    };

    const response = await put<IApiResponse>(
      `${ApiRoute.GIVEAWAYS}/${giveawayToSend.id}/send_giveaway`,
      payload,
    );

    if (response.status === 200) {
      showSuccessBanner(email);
      setFullName('');
      setEmail('');
      setErrorMessage('');
    } else if (response.status === 422) {
      // @ts-expect-error TODO: fix null safety
      setErrorMessage(response.parsedBody.details);
    } else if (response.status === 404 || response.status === 422) {
      showInfobanner();
    }
  };

  const handleSubmit = () => {
    setSubmitted(true);
    if (!email || !fullName) return;

    sendGiveaway();
    setSubmitted(false);
  };

  const handleNameInput = (e) => { setFullName(e.target.value); };

  const handleEmailInput = (e) => { setEmail(e.target.value); };

  return (
    <section className="c-giveaways-send">
      <h3 className="c-heading c-heading--brand-7">
        SEND MY FREE BOX TO:
      </h3>
      <form
        className="simple_form"
        onSubmit={(e) => e.preventDefault()}
      >
        <div className="row">
          <div className="col-md-6">
            <div className={[
              'c-form-group',
              !fullName && isSubmitted && 'is-invalid',
            ].join(' ')}
            >
              <label
                className="string optional"
                htmlFor="giveaway_recipient_name"
              >
                Friend&apos;s Full Name
              </label>
              <input
                id="giveaway_recipient_name"
                className="string optional"
                placeholder="Full Name"
                type="text"
                value={fullName}
                onChange={handleNameInput}
              />
              {!fullName && isSubmitted && (
                <div className="c-form-group__invalid-text">
                  <p> can&apos;t be blank</p>
                </div>
              )}
            </div>

            <div className={[
              'c-form-group', 'email', 'required',
              ((!email && isSubmitted) || errorMessage) && 'is-invalid',
            ].join(' ')}
            >
              <label
                className="string optional"
                htmlFor="giveaway_recipient_email"
              >
                Friend&apos;s Email
              </label>
              <input
                id="giveaway_recipient_email"
                className="string email required"
                placeholder="Email"
                type="email"
                value={email}
                onChange={handleEmailInput}
              />
              {((!email && isSubmitted) || errorMessage) && (
                <div className="c-form-group__invalid-text">
                  { errorMessage || ErrorMessage.INVALID_EMAIL}
                </div>
              )}
            </div>
          </div>
          <div className="col-md-6">
            <div className="c-form-group">
              <div
                className={[
                  'c-form-group',
                  'text',
                  'optional',
                  'subscription_subscription_cancellation_reason_details',
                ].join(' ')}
              >
                <label
                  className="text optional giveaway_personalized_message"
                  htmlFor="giveaway_personal_message"
                >
                  Personalized message (optional)
                </label>
                <textarea
                  className="text optional"
                  id="giveaway_personalized_message"
                  rows={4}
                  cols={60}
                  maxLength={1024}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="col-12">
            <Button
              className="c-button c-button--primary d-block d-md-inline-block"
              onClick={handleSubmit}
              type="submit"
              disabled={false}
            >
              Send Invite
            </Button>
          </div>
        </div>
      </form>
    </section>
  );
}

export default GiveawayForm;
